import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import api from '@/services/apiClient';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

/** Gestão da taxonomia de categorias de equipamento (só admin). */
export default function CategoriesManager({ categories, onChanged }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [err, setErr] = useState(null);

  const run = async (fn) => {
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(e.message);
    }
  };

  const add = () => {
    if (!name.trim()) return;
    run(async () => {
      await api.post('/equipment-categories', { name: name.trim() });
      setName('');
    });
  };

  const rename = () => {
    if (!editName.trim()) return;
    run(async () => {
      await api.put(`/equipment-categories/${editingId}`, { name: editName.trim() });
      setEditingId(null);
    });
  };

  const remove = (c) => {
    if (!confirm(t('equipments.confirmDeleteCategory', { name: c.name }))) return;
    run(() => api.delete(`/equipment-categories/${c.id}`));
  };

  const row = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: 'var(--surf)',
    border: '1px solid var(--line)',
    borderRadius: 8,
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('equipments.categoryNamePlaceholder')}
          style={{ flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button type="button" icon={Plus} onClick={add} disabled={!name.trim()}>{t('equipments.addCategory')}</Button>
      </div>

      {categories.length === 0 && (
        <span style={{ color: 'var(--mid)', fontSize: 13 }}>{t('equipments.noCategories')}</span>
      )}

      {categories.map((c) => (
        <div key={c.id} style={row}>
          {editingId === c.id ? (
            <>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ flex: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); rename(); } }}
                autoFocus
              />
              <Button size="sm" variant="ghost" icon={Check} onClick={rename} />
              <Button size="sm" variant="ghost" icon={X} onClick={() => setEditingId(null)} />
            </>
          ) : (
            <>
              <span style={{ flex: 1, color: 'var(--hi)', fontSize: 14 }}>{c.name}</span>
              <Button size="sm" variant="ghost" icon={Pencil} onClick={() => { setEditingId(c.id); setEditName(c.name); }} />
              <Button size="sm" variant="ghost" icon={Trash2} onClick={() => remove(c)} />
            </>
          )}
        </div>
      ))}

      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
    </div>
  );
}
